// @ts-check
/* eslint-disable jsdoc/require-description */

/** @file Renders and manages the users assigned to a task inside the board task detail modal. */

import _ from "gettext-universal/build/src/translate.js"
import {memo, useEffect, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {ScrollView, Text, View} from "react-native"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"

import Button from "../button"
import Card from "../card"
import UsersSelect from "../users/select"
import useCurrentUser from "../../auth/use-current-user"
import {TaskAssignedUser as RemoteTaskAssignedUser} from "../../frontend-models/task-assigned-user"
import {User as RemoteUser} from "../../frontend-models/user"
import {projectReplicaRowsSource} from "../../database/project-replica-reads"
import LocalTaskAssignedUser from "../../models/task-assigned-user"
import LocalUser from "../../models/user"
import errorMessage from "../../shared/error-message"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useActiveProjectReplica} from "../../sync/project-sync-lifecycle"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskModalUsersProps
 * @property {(count: number) => void} [onCountChange] Reports the loaded assigned-user count to the parent.
 * @property {boolean} [online] Whether online-only assignment commands are available.
 * @property {string} projectId Owning project replica id.
 * @property {{id: () => string | number}} task Task whose assigned users are shown.
 * @property {string} testID Base test id.
 */

/** @typedef {InstanceType<typeof LocalUser> | InstanceType<typeof RemoteUser>} TaskModalUserRecord */

/** @typedef {{kind: "error" | "success", text: string} | null} TaskModalUsersStatus */

/** @typedef {{remoteError: TaskModalUsersStatus, remoteLoadedKey: string, remoteUsers: InstanceType<typeof RemoteUser>[], selectedUserId: string, status: TaskModalUsersStatus}} TaskModalUsersState */

/**
 * Renders the users assigned to a task from tenant-local rows, with add/remove
 * commands and live updates from other sessions.
 * @augments {ShapeComponent<TaskModalUsersProps, TaskModalUsersState>}
 */
class TaskModalUsers extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ReturnType<typeof useCurrentUser>} */
  currentUser = this.hookField()
  /** @type {TaskModalUsersState} */
  state = {remoteError: null, remoteLoadedKey: "", remoteUsers: [], selectedUserId: "", status: null}
  loading = true
  queryStatus = /** @type {TaskModalUsersStatus} */ (null)
  remoteStatus = /** @type {TaskModalUsersStatus} */ (null)
  remoteErrorKey = ""
  /** @type {{assignment: InstanceType<typeof LocalTaskAssignedUser>, user: TaskModalUserRecord | null}[]} */
  rows = []
  count = 0

  /** Connects the selected task to tenant-local assignment and user rows. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.currentUser = useCurrentUser()
    const projectId = this.p.projectId
    const taskID = String(this.p.task.id())
    const replica = useActiveProjectReplica(projectId)
    const assignmentsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalTaskAssignedUser,
      projectId,
      query: (assignments) => assignments.where({taskId: [taskID]}),
      replica
    }), [projectId, replica, taskID])
    const assignmentsState = useLiveQuery(assignmentsSource)
    const userIdsKey = [...new Set(assignmentsState.results.map((assignment) => assignment.userId()).filter(Boolean))].sort().join(",")
    const usersQuery = useMemo(() => userIdsKey ? LocalUser.where({id: userIdsKey.split(",")}) : LocalUser.where({id: []}), [userIdsKey])
    const usersState = useLiveQuery(usersQuery)
    const online = this.p.online ?? true
    const localUserIds = new Set(usersState.results.map((user) => String(user.id())))
    const missingUserIdsKey = [...new Set(assignmentsState.results.map((assignment) => String(assignment.userId())).filter((id) => id && !localUserIds.has(id)))].sort().join(",")
    const remoteLoadedKey = this.s.remoteLoadedKey
    const needsRemoteBackfill = online && missingUserIdsKey.length > 0 && remoteLoadedKey !== missingUserIdsKey
    this.remoteStatus = this.s.remoteError && this.remoteErrorKey === missingUserIdsKey ? this.s.remoteError : null

    const remoteUsersById = Object.fromEntries(this.s.remoteUsers.map((user) => [String(user.id()), user]))
    const usersById = Object.fromEntries(usersState.results.map((user) => [String(user.id()), user]))
    const queryStates = [assignmentsState, usersState]
    const loading = !replica || needsRemoteBackfill || queryStates.some((state) => state.loading)
    const count = assignmentsState.results.length
    const rows = assignmentsState.results
      .map((assignment) => ({
        assignment,
        user: usersById[String(assignment.userId())] || remoteUsersById[String(assignment.userId())] || null
      }))
      .sort((first, second) => this.tt.userLabel(first).localeCompare(this.tt.userLabel(second)))

    const queryError = queryStates.find((state) => state.error)?.error || null
    this.loading = loading
    this.count = count
    this.rows = rows
    this.queryStatus = queryError ? {kind: "error", text: errorMessage(queryError)} : null

    useEffect(() => {
      if (loading) return
      this.p.onCountChange?.(count)
    }, [count, loading])

    useEffect(() => {
      if (!online || missingUserIdsKey.length === 0 || remoteLoadedKey === missingUserIdsKey) return
      let active = true
      void (async () => {
        try {
          const users = await RemoteUser
            .where({id: missingUserIdsKey.split(",")})
            .select(["id", "firstName", "lastName", "email"])
            .toArray()
          if (!active || !this.isMounted()) return
          const existingIds = new Set(this.s.remoteUsers.map((user) => String(user.id())))
          const freshUsers = users.filter((user) => !existingIds.has(String(user.id())))
          if (freshUsers.length > 0) this.s.remoteUsers = [...this.s.remoteUsers, ...freshUsers]
          this.s.remoteError = null
        } catch (error) {
          if (!active || !this.isMounted()) return
          this.remoteErrorKey = missingUserIdsKey
          this.s.remoteError = {kind: "error", text: errorMessage(/** @type {Error} */ (error))}
        } finally {
          if (active && this.isMounted()) this.s.remoteLoadedKey = missingUserIdsKey
        }
      })()
      return () => { active = false }
    }, [online, missingUserIdsKey, remoteLoadedKey])
  }

  /** @returns {ReactNode} Users tab content. */
  render() {
    const {selectedUserId, status: commandStatus} = this.s
    const {testID} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    const online = this.p.online ?? true
    const status = this.queryStatus || this.remoteStatus || commandStatus
    const assignedUserIds = this.rows.map((row) => String(row.assignment.userId()))
    return (
      <View style={styles.container ||= {flexShrink: 1, minHeight: 0}} testID={`${testID}/users`}>
        <Text style={styles.title ||= {color: colors.text, fontSize: 18, fontWeight: 700, marginBottom: 16}} testID={`${testID}/users/title`}>{_("Users")}</Text>
        {online &&
          <Card
            style={styles.addRow ||= {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 16
            }}
            testID={`${testID}/users/addRow`}
          >
            <View style={styles.selectWrapper ||= {flex: 1, minWidth: 200}} testID={`${testID}/users/addRow/select`}>
              <UsersSelect
                excludeValues={assignedUserIds}
                includeEmailInLabel
                onChangeValue={this.tt.onUserSelectChange}
                placeholder={_("Select user")}
                testID={`${testID}/users/addRow/select/field`}
                values={selectedUserId ? [selectedUserId] : []}
              />
            </View>
            <Button
              disabled={!selectedUserId}
              iconName="plus"
              label={_("Add user")}
              onPress={this.tt.onAddPress}
              testID={`${testID}/users/addRow/addButton`}
              variant="neutral"
            />
          </Card>
        }
        <ScrollView contentContainerStyle={styles.scrollContent ||= {paddingBottom: 4}} style={styles.scroll ||= {flexShrink: 1}} testID={`${testID}/users/scroll`}>
          {status &&
            <Text
              style={styles[`status-${themeName}-${status.kind}`] ||= {
                color: status.kind === "error" ? colors.dangerText : colors.successText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12
              }}
              testID={`${testID}/users/status`}
            >
              {status.text}
            </Text>
          }
          {this.loading && this.rows.length === 0 &&
            <Text style={styles[`loading-${themeName}`] ||= {color: colors.textMuted}} testID={`${testID}/users/loading`}>{_("Loading...")}</Text>
          }
          {!this.loading && !status && this.rows.length === 0 &&
            <Text style={styles[`empty-${themeName}`] ||= {color: colors.textMuted}} testID={`${testID}/users/empty`}>{_("No users assigned to this task")}</Text>
          }
          {this.rows.map((row) => this.tt.renderRow(row, testID, themeName))}
        </ScrollView>
      </View>
    )
  }

  /**
   * Applies the selected user from the shared user select.
   * @param {(string | number)[] | string | number | undefined} selected Selected value.
   */
  onUserSelectChange = (selected) => {
    const selectedUserId = Array.isArray(selected)
      ? selected[0] ? String(selected[0]) : ""
      : selected ? String(selected) : ""
    this.s.selectedUserId = selectedUserId
    this.s.status = null
  }

  /**
   * Assigns the selected user to the task; local convergence follows through SyncClient.
   * @returns {Promise<void>} Resolves when add handling completes.
   */
  onAddPress = async () => {
    const {selectedUserId} = this.s
    if (!selectedUserId) return
    const taskID = String(this.p.task.id())
    const assignment = new RemoteTaskAssignedUser()
    assignment.setTaskId(taskID)
    assignment.setUserId(selectedUserId)
    try {
      await assignment.save()
      this.s.status = {kind: "success", text: _("User added to task")}
      this.s.selectedUserId = ""
    } catch (error) {
      this.s.status = {kind: "error", text: errorMessage(error)}
    }
  }

  /**
   * Removes a user assignment from the task; local convergence follows through SyncClient.
   * @param {string} assignmentId Assignment id to remove.
   * @returns {Promise<void>} Resolves when remove handling completes.
   */
  onRemovePress = async (assignmentId) => {
    const assignment = new RemoteTaskAssignedUser()
    assignment.setId(assignmentId)
    try {
      await assignment.destroy()
      this.s.status = {kind: "success", text: _("User removed from task")}
    } catch (error) {
      this.s.status = {kind: "error", text: errorMessage(error)}
    }
  }

  /**
   * Builds the display label for an assigned user.
   * @param {{assignment: InstanceType<typeof LocalTaskAssignedUser>, user: TaskModalUserRecord | null}} row Joined assignment and user.
   * @returns {string} User name or email.
   */
  userLabel(row) {
    const userId = String(row.assignment.userId())
    if (!row.user) return userId
    const name = [row.user.firstName(), row.user.lastName()].filter(Boolean).join(" ").trim()
    return name || row.user.email() || userId
  }

  /**
   * Renders one assigned user row with a remove action and an optional "you" badge.
   * @param {{assignment: InstanceType<typeof LocalTaskAssignedUser>, user: TaskModalUserRecord | null}} row Joined assignment and user.
   * @param {string} testID Base test id.
   * @param {string} themeName Active theme name.
   * @returns {ReactNode} Rendered row.
   */
  renderRow(row, testID, themeName) {
    const colors = this.tt.colors
    const online = this.p.online ?? true
    const name = this.tt.userLabel(row)
    const email = row.user?.email() || ""
    const isCurrentUser = this.tt.currentUser.user ? String(row.assignment.userId()) === String(this.tt.currentUser.user.id) : false
    const rowID = String(row.assignment.id())
    return (
      <Card
        key={rowID}
        style={styles.row ||= {
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 8
        }}
        testID={`${testID}/users/row-${rowID}`}
      >
        <View style={styles.rowText ||= {flex: 1, paddingRight: 8}} testID={`${testID}/users/row-${rowID}/text`}>
          <View style={styles.rowNameRow ||= {alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: 8}}>
            <Text style={styles.rowName ||= {color: colors.text, fontSize: 16, fontWeight: 600}} testID={`${testID}/users/row-${rowID}/name`}>{name}</Text>
            {isCurrentUser &&
              <View style={styles.youBadge ||= {alignItems: "center", backgroundColor: colors.infoBackground, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2}}>
                <Text style={styles[`youBadgeText-${themeName}`] ||= {color: colors.link, fontSize: 11, fontWeight: 700}} testID={`${testID}/users/row-${rowID}/youBadge`}>{_("You")}</Text>
              </View>
            }
          </View>
          {email ?
            <Text style={styles[`rowEmail-${themeName}`] ||= {color: colors.textMuted, fontSize: 13}} testID={`${testID}/users/row-${rowID}/email`}>{email}</Text>
            : null
          }
        </View>
        {online &&
          <Button
            iconName="trash"
            label={_("Remove")}
            onPress={() => this.tt.onRemovePress(rowID)}
            testID={`${testID}/users/row-${rowID}/removeButton`}
            variant="destructive"
          />
        }
      </Card>
    )
  }
}

/** @type {NamedExoticComponent<TaskModalUsersProps>} */
export default memo(shapeComponent(TaskModalUsers))
